import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, imgUrl, money } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import SafeImage from "../../components/common/SafeImage";
import ColorValue from "../../components/store/ColorValue";
import { toast } from "sonner";
import { ShoppingBag, Share2, Heart, Clock } from "lucide-react";

export default function SharedCartView() {
  const { token } = useParams();
  const { user } = useAuth();
  const { refresh } = useCart();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const r = await api.get(`/cart/shared/${token}`);
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) {
          const status = e?.response?.status;
          if (status === 410) setError("This shared bag has expired.");
          else setError(e?.response?.data?.detail || "This shared bag is no longer available.");
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const doImport = async () => {
    if (!user) {
      toast.info("Sign in to import this bag into yours.");
      // Preserve the target so we can bounce back after auth
      localStorage.setItem("kb_post_login_redirect", `/cart/shared/${token}`);
      return;
    }
    setImporting(true);
    try {
      const r = await api.post(`/cart/shared/${token}/import`);
      toast.success(
        `${r.data.added} item${r.data.added === 1 ? "" : "s"} added to your bag` +
          (r.data.skipped_unavailable ? ` · ${r.data.skipped_unavailable} no longer available` : ""),
      );
      await refresh();
      navigate("/cart");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not import bag");
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="px-6 md:px-12 lg:px-24 py-20" data-testid="shared-cart-loading">
        <div className="max-w-2xl mx-auto space-y-4">
          <div className="h-6 bg-[hsl(var(--kb-surface-alt))] animate-pulse w-40" />
          <div className="h-12 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
          <div className="h-32 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
          <div className="h-32 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 py-24 text-center" data-testid="shared-cart-error">
        <div className="overline mb-3">Shared bag</div>
        <h1 className="font-display text-4xl mb-4">Link no longer available</h1>
        <p className="text-[hsl(var(--kb-muted))] mb-8 max-w-md mx-auto">{error}</p>
        <Link to="/shop" className="kb-btn-primary inline-block">Browse the Bazaar</Link>
      </div>
    );
  }

  const items = data?.items || [];
  const expires = data?.expires_at ? new Date(data.expires_at) : null;
  const expiresLabel = expires
    ? expires.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : null;

  return (
    <div className="px-6 md:px-12 lg:px-24 py-10" data-testid="shared-cart-page">
      <div className="max-w-3xl mx-auto">
        <div className="overline mb-2 flex items-center gap-2">
          <Share2 size={14} /> Shared with you
        </div>
        <h1 className="font-display text-4xl md:text-5xl mb-3">
          <span className="text-[hsl(var(--kb-primary))]">{data?.created_by_name || "A friend"}</span> shared a bag
        </h1>
        {data?.note && (
          <blockquote
            className="border-l-2 border-[hsl(var(--kb-primary))] pl-4 italic text-[hsl(var(--kb-muted))] my-5 max-w-xl"
            data-testid="shared-cart-note"
          >
            "{data.note}"
          </blockquote>
        )}
        {expiresLabel && (
          <div className="text-xs text-[hsl(var(--kb-muted))] flex items-center gap-1 mb-8">
            <Clock size={12} /> Available until {expiresLabel}
          </div>
        )}

        {items.length === 0 ? (
          <div className="border border-dashed border-[hsl(var(--kb-border))] py-10 text-center text-[hsl(var(--kb-muted))] text-sm">
            This bag is empty.
          </div>
        ) : (
          <div className="space-y-4 mb-10">
            {items.map((item, i) => (
              <div
                key={i}
                className="flex gap-4 border border-[hsl(var(--kb-border))] p-4"
                data-testid={`shared-item-${item.product_id}`}
              >
                <Link
                  to={`/product/${item.product?.slug}`}
                  className="w-24 sm:w-28 aspect-[3/4] overflow-hidden bg-[hsl(var(--kb-surface-alt))] shrink-0"
                >
                  <SafeImage src={imgUrl(item.product?.images?.[0])} alt="" className="w-full h-full object-cover" />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/product/${item.product?.slug}`}
                    className="font-display text-lg sm:text-xl line-clamp-2"
                  >
                    {item.product?.title}
                  </Link>
                  {item.color && (
                    <div className="text-xs text-[hsl(var(--kb-muted))] mt-1" data-testid={`shared-line-color-${item.product_id}`}>
                      Colour: <ColorValue value={item.color} />
                    </div>
                  )}
                  {item.size && (
                    <div className="text-xs text-[hsl(var(--kb-muted))] mt-1">Size: {item.size}</div>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-sm">
                    {/* Variant lines show the variant's own price (server-resolved). */}
                    <span className="text-[hsl(var(--kb-primary))]">{money(item.unit_price ?? item.product?.price)}</span>
                    <span className="text-[hsl(var(--kb-muted))]">× {item.qty}</span>
                  </div>
                  {item.product?.seller?.shop_name && (
                    <div className="text-xs text-[hsl(var(--kb-muted))] mt-2 truncate">
                      From {item.product.seller.shop_name}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <div className="bg-white border border-[hsl(var(--kb-border))] p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="overline mb-1">Bag total</div>
              <div className="font-display text-3xl" data-testid="shared-cart-subtotal">
                {money(data?.subtotal || 0)}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Link to="/shop" className="kb-btn-outline flex items-center gap-2">
                <Heart size={14} /> Keep browsing
              </Link>
              <button
                onClick={doImport}
                disabled={importing}
                className="kb-btn-primary flex items-center gap-2 disabled:opacity-60"
                data-testid="shared-cart-import-btn"
              >
                <ShoppingBag size={14} />
                {importing ? "Adding…" : user ? "Add all to my bag" : "Sign in to import"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
